import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'

interface PartRow {
  id: string
  name: string
  unit: string
  isActive: boolean
  totalQuantity: number
  stockByPoint: { pointId: string; pointName: string; quantity: number }[]
}

interface MovementRow {
  id: string
  type: 'receipt' | 'transfer' | 'writeoff'
  partName: string
  unit: string
  quantity: number
  unitCost: number
  totalCost: number
  fromPoint: string | null
  toPoint: string | null
  bikeCode: string | null
  note: string | null
  createdBy: string
  createdAt: string
}

const TYPE_LABEL: Record<string, string> = {
  receipt: '📥 Приход',
  transfer: '🔄 Перемещение',
  writeoff: '🔧 Списание',
}

const fmtMoney = (n: number) => n.toLocaleString('ru-RU') + ' ₽'
const fmtDate = (s: string) => new Date(s).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

export default function WarehousePage() {
  const qc = useQueryClient()
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner'
  const [tab, setTab] = useState<'stock' | 'history'>('stock')
  const [showNewPart, setShowNewPart] = useState(false)
  const [showReceipt, setShowReceipt] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showWriteoff, setShowWriteoff] = useState(false)

  const { data: partsData, isLoading } = useQuery<{ parts: PartRow[] }>({
    queryKey: ['warehouse-parts'],
    queryFn: () => api.get('/warehouse/parts').then((r) => r.data),
  })
  const parts = partsData?.parts ?? []

  const { data: movData } = useQuery<{ movements: MovementRow[] }>({
    queryKey: ['warehouse-movements'],
    queryFn: () => api.get('/warehouse/movements', { params: { limit: 100 } }).then((r) => r.data),
    enabled: tab === 'history',
  })
  const movements = movData?.movements ?? []

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['warehouse-parts'] })
    qc.invalidateQueries({ queryKey: ['warehouse-movements'] })
  }

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Склад запчастей</h1>
        <div className="flex flex-wrap gap-2">
          {isOwner && <button onClick={() => setShowReceipt(true)} className="bg-brand-600 text-white text-sm px-3 py-2 rounded-xl font-medium">📥 Приход</button>}
          <button onClick={() => setShowTransfer(true)} disabled={parts.length === 0} className="border border-gray-300 text-sm px-3 py-2 rounded-xl disabled:opacity-50">🔄 Перемещение</button>
          <button onClick={() => setShowWriteoff(true)} disabled={parts.length === 0} className="border border-gray-300 text-sm px-3 py-2 rounded-xl disabled:opacity-50">🔧 Списание</button>
          {isOwner && <button onClick={() => setShowNewPart(true)} className="border border-gray-300 text-sm px-3 py-2 rounded-xl">+ Запчасть</button>}
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 w-fit">
        <button onClick={() => setTab('stock')} className={`text-sm px-4 py-1.5 rounded-xl ${tab === 'stock' ? 'bg-white shadow-sm font-medium' : 'text-gray-500'}`}>Остатки</button>
        <button onClick={() => setTab('history')} className={`text-sm px-4 py-1.5 rounded-xl ${tab === 'history' ? 'bg-white shadow-sm font-medium' : 'text-gray-500'}`}>История</button>
      </div>

      {tab === 'stock' && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {isLoading && <p className="text-gray-400 text-center py-8">Загрузка...</p>}
          {!isLoading && parts.length === 0 && (
            <p className="text-gray-400 text-center py-10">Запчастей пока нет. Добавьте через «+ Запчасть» и оформите приход.</p>
          )}
          {parts.map((p) => (
            <div key={p.id} className="border-b border-gray-100 last:border-0 px-4 py-3">
              <div className="flex justify-between items-start">
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-sm font-bold">{p.totalQuantity} {p.unit}</p>
              </div>
              {p.stockByPoint.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {p.stockByPoint.map((s) => (
                    <span key={s.pointId} className="text-xs text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
                      {s.pointName}: {s.quantity}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'history' && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {movements.length === 0 && <p className="text-gray-400 text-center py-10">Движений пока нет</p>}
          {movements.map((m) => (
            <div key={m.id} className="border-b border-gray-100 last:border-0 px-4 py-3 text-sm">
              <div className="flex justify-between">
                <span>{TYPE_LABEL[m.type] ?? m.type}</span>
                <span className="text-gray-400 text-xs">{fmtDate(m.createdAt)}</span>
              </div>
              <p className="mt-0.5">
                <strong>{m.partName}</strong> · {m.quantity} {m.unit}
                {m.type === 'receipt' && m.unitCost > 0 && <span className="text-gray-500"> · {fmtMoney(m.totalCost)}</span>}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {m.fromPoint && <>из {m.fromPoint} </>}
                {m.toPoint && <>→ {m.toPoint} </>}
                {m.bikeCode && <>· велик #{m.bikeCode} </>}
                · {m.createdBy}
              </p>
              {m.note && <p className="text-xs text-gray-400 mt-0.5">{m.note}</p>}
            </div>
          ))}
        </div>
      )}

      {showNewPart && <NewPartModal onClose={() => setShowNewPart(false)} onDone={() => { setShowNewPart(false); refresh() }} />}
      {showReceipt && <ReceiptModal parts={parts} onClose={() => setShowReceipt(false)} onDone={() => { setShowReceipt(false); refresh() }} />}
      {showTransfer && <TransferModal parts={parts} onClose={() => setShowTransfer(false)} onDone={() => { setShowTransfer(false); refresh() }} />}
      {showWriteoff && <WriteoffModal parts={parts} onClose={() => setShowWriteoff(false)} onDone={() => { setShowWriteoff(false); refresh() }} />}
    </div>
  )
}

/** Available quantity of a part at a given point (from the stock breakdown). */
function availableAt(part: PartRow | undefined, pointId: string): number {
  if (!part || !pointId) return 0
  return part.stockByPoint.find((s) => s.pointId === pointId)?.quantity ?? 0
}

function TransferModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []
  const [partId, setPartId] = useState('')
  const [fromPointId, setFromPointId] = useState('')
  const [toPointId, setToPointId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const part = parts.find((p) => p.id === partId)
  const avail = availableAt(part, fromPointId)
  const qty = parseInt(quantity, 10)
  const canSubmit = !!partId && !!fromPointId && !!toPointId && fromPointId !== toPointId && qty > 0 && qty <= avail

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/transfer', { partId, fromPointId, toPointId, quantity: qty, note: note.trim() || undefined }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Перемещение запчастей" onClose={onClose}>
      <label className="block text-xs text-gray-600">Запчасть
        <select value={partId} onChange={(e) => setPartId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.name} (всего {p.totalQuantity})</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600">Откуда
          <select value={fromPointId} onChange={(e) => setFromPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— выберите —</option>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="block text-xs text-gray-600">Куда
          <select value={toPointId} onChange={(e) => setToPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— выберите —</option>
            {points.filter((p) => p.id !== fromPointId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      {partId && fromPointId && <p className="text-xs text-gray-500">Доступно на точке: <strong>{avail}</strong></p>}
      <label className="block text-xs text-gray-600">Количество
        <input type="number" min={1} max={avail} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      <label className="block text-xs text-gray-600">Заметка
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={!canSubmit || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Переместить" />
    </Modal>
  )
}

function WriteoffModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []
  const { data: bikesData } = useQuery({ queryKey: ['bikes'], queryFn: () => api.get('/bikes').then((r) => r.data) })
  const bikes: { id: string; code: string; model?: string | null }[] = bikesData?.bikes ?? []
  const [partId, setPartId] = useState('')
  const [pointId, setPointId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [bikeId, setBikeId] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const part = parts.find((p) => p.id === partId)
  const avail = availableAt(part, pointId)
  const qty = parseInt(quantity, 10)
  const canSubmit = !!partId && !!pointId && qty > 0 && qty <= avail

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/writeoff', {
      partId, pointId, quantity: qty,
      bikeId: bikeId || undefined,
      note: note.trim() || undefined,
    }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Списание запчасти" onClose={onClose}>
      <label className="block text-xs text-gray-600">Запчасть
        <select value={partId} onChange={(e) => setPartId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.name} (всего {p.totalQuantity})</option>)}
        </select>
      </label>
      <label className="block text-xs text-gray-600">Точка (откуда списываем)
        <select value={pointId} onChange={(e) => setPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      {partId && pointId && <p className="text-xs text-gray-500">Доступно на точке: <strong>{avail}</strong></p>}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600">Количество
          <input type="number" min={1} max={avail} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
        <label className="block text-xs text-gray-600">Велосипед (необязательно)
          <select value={bikeId} onChange={(e) => setBikeId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— без привязки —</option>
            {bikes.map((b) => <option key={b.id} value={b.id}>#{b.code}{b.model ? ` · ${b.model}` : ''}</option>)}
          </select>
        </label>
      </div>
      <label className="block text-xs text-gray-600">Заметка (причина ремонта)
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={!canSubmit || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Списать" />
    </Modal>
  )
}

function NewPartModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('шт')
  const [error, setError] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: () => api.post('/warehouse/parts', { name: name.trim(), unit: unit.trim() || 'шт' }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Не удалось создать запчасть'),
  })
  return (
    <Modal title="Новая запчасть" onClose={onClose}>
      <label className="block text-xs text-gray-600">Название
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      <label className="block text-xs text-gray-600">Единица
        <input value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={name.trim().length < 1 || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Создать" />
    </Modal>
  )
}

function ReceiptModal({ parts, onClose, onDone }: { parts: PartRow[]; onClose: () => void; onDone: () => void }) {
  const { data: pointsData } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const points: { id: string; name: string }[] = pointsData?.points ?? []
  const [partId, setPartId] = useState('')
  const [pointId, setPointId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const qty = parseInt(quantity, 10)
  const cost = parseFloat(unitCost)
  const canSubmit = !!partId && !!pointId && qty > 0 && !isNaN(cost) && cost >= 0

  const m = useMutation({
    mutationFn: () => api.post('/warehouse/receipt', { partId, pointId, quantity: qty, unitCost: cost, note: note.trim() || undefined }),
    onSuccess: onDone,
    onError: (e: { response?: { data?: { error?: { message?: string } } } }) => setError(e?.response?.data?.error?.message ?? 'Ошибка'),
  })

  return (
    <Modal title="Приход запчастей" onClose={onClose}>
      <label className="block text-xs text-gray-600">Запчасть
        <select value={partId} onChange={(e) => setPartId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="block text-xs text-gray-600">Точка (куда приходит)
        <select value={pointId} onChange={(e) => setPointId(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
          <option value="">— выберите —</option>
          {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600">Количество
          <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
        <label className="block text-xs text-gray-600">Цена за ед., ₽
          <input type="number" min={0} step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
      </div>
      <label className="block text-xs text-gray-600">Заметка
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="(необязательно)" className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <SubmitRow onClose={onClose} disabled={!canSubmit || m.isPending} pending={m.isPending} onSubmit={() => { setError(null); m.mutate() }} label="Оформить приход" />
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function SubmitRow({ onClose, onSubmit, disabled, pending, label }: { onClose: () => void; onSubmit: () => void; disabled: boolean; pending: boolean; label: string }) {
  return (
    <div className="flex gap-2 pt-1">
      <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm">Отмена</button>
      <button onClick={onSubmit} disabled={disabled} className="flex-1 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium disabled:opacity-50">
        {pending ? 'Сохранение…' : label}
      </button>
    </div>
  )
}
